import { PlayCircleOutlined, PlusOutlined } from '@ant-design/icons';
import { ModalForm, ProFormSelect, ProTable } from '@ant-design/pro-components';
import { useMutation } from '@tanstack/react-query';
import { App, Button, Popconfirm } from 'antd';
import PropTypes from 'prop-types';
import { useMemo, useRef } from 'react';
import { CronFormItem, formatCronDescription } from '../components/CronEditor';
import {
  createAgentSchedule,
  queryAgentSchedules,
  queryAvailableAgents,
  removeAgentSchedule,
  runAgentSchedule,
  updateAgentSchedule,
} from '../services/agents';
import { queryCollections } from '../services/collect';

const SCHEDULE_STATUS = {
  active: { text: '启用', status: 'Success' },
  archived: { text: '归档', status: 'Default' },
  paused: { text: '暂停', status: 'Warning' },
};

const statusOptions = [
  { label: '启用', value: 'active' },
  { label: '暂停', value: 'paused' },
  { label: '归档', value: 'archived' },
];

const AgentScheduleTable = ({ agent, allowCreate = false, onRun, organizationId }) => {
  const actionRef = useRef(null);
  const { message } = App.useApp();
  const reload = () => actionRef.current?.reload?.();
  const isCollectionAgent = agent?.configuration?.type === 'collection';

  const createMutation = useMutation({
    mutationFn: createAgentSchedule,
    onSuccess: () => {
      message.success('定时任务已创建');
      reload();
    },
    onError: () => message.error('定时任务创建失败，请重试'),
  });
  const updateMutation = useMutation({
    mutationFn: updateAgentSchedule,
    onSuccess: () => {
      message.success('定时任务已更新');
      reload();
    },
    onError: () => message.error('定时任务更新失败，请重试'),
  });
  const removeMutation = useMutation({
    mutationFn: removeAgentSchedule,
    onSuccess: () => {
      message.success('定时任务已删除');
      reload();
    },
    onError: () => message.error('定时任务删除失败，请重试'),
  });
  const runMutation = useMutation({
    mutationFn: runAgentSchedule,
    onSuccess: () => {
      message.success('Agent Run 已提交');
      onRun?.();
    },
    onError: () => message.error('Agent Run 提交失败，请重试'),
  });

  const columns = useMemo(() => {
    const items = [];
    if (!agent) {
      items.push({
        title: '智能体',
        dataIndex: 'agentTitle',
        valueType: 'select',
        renderText: (_, record) => record.agentTitle || record.agentId,
        fieldProps: { fieldNames: { label: 'title', value: 'agentId' } },
        request: () => queryAvailableAgents({ organizationId }),
        search: { transform: (value) => ({ agentId: value }) },
      });
    }
    items.push(
      {
        title: '采集配置',
        dataIndex: 'collectionName',
        renderText: (value) => value || '-',
        search: false,
      },
      {
        title: '定时规则',
        dataIndex: 'cron',
        renderText: (cron) => formatCronDescription(cron),
        search: false,
      },
      {
        title: '状态',
        dataIndex: 'status',
        valueType: 'select',
        valueEnum: SCHEDULE_STATUS,
        fieldProps: { options: statusOptions },
      },
      {
        title: '创建时间',
        dataIndex: 'createdAt',
        valueType: 'dateTime',
        search: false,
      },
      {
        title: '操作',
        valueType: 'option',
        render: (_, record) => [
          <Button
            key="run"
            type="link"
            icon={<PlayCircleOutlined />}
            disabled={record.status === 'archived'}
            loading={runMutation.isPending}
            onClick={() => runMutation.mutateAsync({ data: { id: record.id, organizationId } })}
          >
            运行
          </Button>,
          <ModalForm
            key="edit"
            title="修改定时任务"
            trigger={<Button type="link">修改</Button>}
            width={480}
            initialValues={record}
            modalProps={{ destroyOnHidden: true }}
            onFinish={async (values) => {
              await updateMutation.mutateAsync({
                data: { id: record.id, organizationId, ...values },
              });
              return true;
            }}
          >
            <CronFormItem />
            <ProFormSelect
              name="status"
              label="状态"
              options={statusOptions}
              rules={[{ required: true, message: '请选择状态' }]}
            />
          </ModalForm>,
          <Popconfirm
            key="remove"
            title="删除定时任务"
            description="确认删除这个定时任务？"
            okText="确认"
            cancelText="取消"
            onConfirm={() =>
              removeMutation.mutateAsync({ data: { id: record.id, organizationId } })
            }
          >
            <Button type="link" danger loading={removeMutation.isPending}>
              删除
            </Button>
          </Popconfirm>,
        ],
      },
    );
    return items;
  }, [agent, organizationId, removeMutation, runMutation, updateMutation]);

  return (
    <ProTable
      actionRef={actionRef}
      rowKey="id"
      headerTitle="定时任务"
      request={(params) =>
        queryAgentSchedules({
          ...params,
          agentId: agent?.agentId,
          organizationId,
        })
      }
      columns={columns}
      search={{ labelWidth: 96 }}
      toolBarRender={
        allowCreate
          ? () => [
              <ModalForm
                key="create"
                title="创建定时任务"
                trigger={
                  <Button type="primary" icon={<PlusOutlined />}>
                    创建定时任务
                  </Button>
                }
                width={480}
                modalProps={{ destroyOnHidden: true }}
                onFinish={async (values) => {
                  await createMutation.mutateAsync({
                    data: { ...values, agentId: agent.agentId, organizationId },
                  });
                  return true;
                }}
              >
                {isCollectionAgent ? (
                  <ProFormSelect
                    name="collectionId"
                    label="采集配置"
                    request={async () => {
                      const result = await queryCollections({
                        collectionType: agent.configuration.collectionType,
                        organizationId,
                        pageSize: 100,
                      });
                      return result.data.map(({ id, name }) => ({ label: name, value: id }));
                    }}
                    rules={[{ required: true, message: '请选择采集配置' }]}
                  />
                ) : null}
                <CronFormItem />
                <ProFormSelect
                  name="status"
                  label="状态"
                  initialValue="active"
                  options={statusOptions}
                  rules={[{ required: true, message: '请选择状态' }]}
                />
              </ModalForm>,
            ]
          : undefined
      }
    />
  );
};

AgentScheduleTable.propTypes = {
  agent: PropTypes.shape({
    agentId: PropTypes.string.isRequired,
    configuration: PropTypes.shape({
      collectionType: PropTypes.string,
      type: PropTypes.string,
    }),
  }),
  allowCreate: PropTypes.bool,
  onRun: PropTypes.func,
  organizationId: PropTypes.string.isRequired,
};

export default AgentScheduleTable;
